import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { clearDialogData, getDialogData, selectDialogAudio, selectDialogData } from "@/store/ses/sesArchiveSlice";
import useAccessRight from "@/hooks/useAccessRight";
import useTranslate from "@/hooks/useTranslate";
import { SES } from "@/constants/accessRights";
import { RequestStatus, ResponseStatus } from "@/types/statusTypes";
import FormAddingScript from "@/components/Forms/FormAddingScript/FormAddingScript";
import { BlockWithError, BlockWithLoading, ButtonCopyToClipboard, CustomButton, H3 } from "@/ui";
import NoticeSingleAction from "../../../ui/NoticeSingleAction/NoticeSingleAction";
import Message from "./Message/Message";
import { IMessagesProps } from "./Messages.props";
import styles from "./Messages.module.scss";

const Messages = ({ selectedPairOfMessages, setSelectedPairOfMessages, selectedDialogue, service = "ses" }: IMessagesProps): JSX.Element => {
	const [showModalAddScript, setShowModalAddScript] = useState<boolean>(false); // показ формы добавления скрипта
	const [showNotificationAddScript, setShowNotificationAddScript] = useState<boolean>(false); // показ уведомления добавления скрипта

	const listRef = useRef<HTMLUListElement | null>(null); // ссылка на список сообщений
	const [followDialogueFlg, setFollowDialogueFlg] = useState<boolean>(false); // подписка на обновление данных диалога

	const dispatch = useAppDispatch();
	const dialogData = useAppSelector(selectDialogData); // store - данные диалога
	const dialogAudio = useAppSelector(selectDialogAudio); // store - аудио диалога

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// следим за флагом подписки
	useEffect(() => {
		let interval: NodeJS.Timer;
		// если стоит флаг
		if (followDialogueFlg) {
			interval = setInterval(() => {
				isAccess(SES.ARCHIVE_DIALOG) && dispatch(getDialogData(selectedDialogue.uuid)); // получение данных диалога
			}, 5000);
		}
		return () => {
			clearInterval(interval);
		};
	}, [followDialogueFlg]);

	// следим за выбранной ВОП сообщений
	useEffect(() => {
		if (typeof selectedPairOfMessages === "number") {
			listRef.current?.children[selectedPairOfMessages]?.scrollIntoView({ block: "center", behavior: "smooth" });
		}
	}, [selectedPairOfMessages]);

	// следим за выбранным диалогом
	useEffect(() => {
		// сбрасываем подписку
		if (followDialogueFlg) setFollowDialogueFlg(false);
	}, [selectedDialogue]);

	// следим за данными диалога
	useEffect(() => {
		// при активной подписке
		if (Array.isArray(dialogData.data) && followDialogueFlg) {
			// прокрутка в конец списка сообщений
			dialogData.data?.length > 0 && listRef.current?.children[dialogData.data.length - 1]?.scrollIntoView({ block: "center", behavior: "smooth" });
		}
	}, [dialogData.data]);

	// обновить данные
	const updateData = useCallback(() => {
		dispatch(clearDialogData()); // очистка данных диалога
		isAccess(SES.ARCHIVE_DIALOG) && dispatch(getDialogData(selectedDialogue.uuid)); // получение данных диалога
	}, []);

	return (
		<>
			{/* загрузка */}
			{dialogData.status === RequestStatus.LOADING && !followDialogueFlg && <BlockWithLoading />}

			{/* ошибка */}
			{(dialogData.status === RequestStatus.FAILED || dialogData.error === ResponseStatus.FAILED) && (
				<BlockWithError message={dialogData.message === "dialog not found" ? "title_dialogNotFound" : dialogData.message} updateHandler={updateData} />
			)}

			{(dialogData.status === RequestStatus.IDLE || followDialogueFlg) && Array.isArray(dialogData.data) && (
				<div className={styles.messageContainer}>
					{service === "ses" && (
						<div className={styles.messageHeader}>
							<H3>
								{translate("title_dialogueId")}: {selectedDialogue.uuid}
							</H3>
							<ButtonCopyToClipboard text={selectedDialogue.uuid} />
						</div>
					)}

					<ul className={styles.messageList} ref={listRef}>
						{dialogData.data.map((messageData, idx, array) => {
							// для ses
							if (service === "ses" && typeof selectedPairOfMessages === "number" && setSelectedPairOfMessages) {
								// если временная метка подходит для этой ВОП
								if (dialogAudio.dataUrl && messageData.startRequest <= dialogAudio.timestamp && dialogAudio.timestamp <= messageData.endReply) {
									// и предыдущая активная ВОП не подходит для той же временной метки
									if (!(array[selectedPairOfMessages].startRequest <= dialogAudio.timestamp && dialogAudio.timestamp <= array[selectedPairOfMessages].endReply)) {
										setSelectedPairOfMessages(idx); // меняем индекс в state
									}
								}
							}
							return (
								<Message
									key={idx}
									messageData={messageData}
									idx={idx}
									isSelectedPair={selectedPairOfMessages === idx} // кэшируем
									setSelectedPairOfMessages={setSelectedPairOfMessages}
									service={service}
								/>
							);
						})}
					</ul>

					{service === "ses" && (
						<div className={styles.block}>
							<CustomButton
								disabled={dialogData.data.length === 0 || Object.keys(dialogData.data[0].request || {}).length === 0}
								onClick={() => setShowModalAddScript(true)}
								sx={{ flexGrow: 1 }}
							>
								{translate("button_addToTestScript")}
							</CustomButton>
							<CustomButton onClick={() => setFollowDialogueFlg((prev) => !prev)} sx={{ flexGrow: 1 }}>
								{translate(followDialogueFlg ? "button_stopObservation" : "button_followDialogue")}
							</CustomButton>
						</div>
					)}
				</div>
			)}

			{/* форма добавления скрипта */}
			{showModalAddScript && <FormAddingScript showModal={showModalAddScript} setShowModal={setShowModalAddScript} setShowNotification={setShowNotificationAddScript} />}

			<NoticeSingleAction
				showNotification={showNotificationAddScript}
				setShowNotification={setShowNotificationAddScript}
				title="noticeAddition_success"
				severity="info"
			/>
		</>
	);
};

export default memo(Messages);
